import { useState } from "react";
import { T } from "../../theme";
import { REL } from "../../theme";
import { usePeople } from "../../context/PeopleContext";
import { supabase } from "../../supabaseClient.js";
import { Tag, Tog } from "../ui/index";
import { PersonAgreements, PRIVACY_OPTS, PERSON_NOTES, PERSON_UPCOMING } from "../agreements/index";
import { fmtAnniversary, fmtBirthday } from "../../utils/helpers";
import { HOME_WEEK_DAYS, WEEK_DATE_NUMS, HOME_EVENTS, HOME_TODAY, CURRENT_MONTH_NAME } from "../../utils/calendarUtils";

function _fmtLastSeen(lastActive) {
  if (!lastActive) return null;
  const ms = Date.now() - new Date(lastActive).getTime();
  const days = Math.floor(ms / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

export function RemovePersonDialog({ person, onConfirm, onCancel }) {
  const c = REL[person.type].color;
  return (
    <div onClick={onCancel} style={{position:"fixed",inset:0,background:"rgba(30,16,8,0.55)",zIndex:600,display:"flex",alignItems:"flex-end",animation:"fadeIn .15s ease"}}>
      <div onClick={e=>e.stopPropagation()} style={{width:"100%",maxWidth:500,margin:"0 auto",background:T.surface,borderRadius:"20px 20px 0 0",padding:"28px 24px 40px",animation:"fadeUp .2s ease"}}>
        <div style={{width:36,height:4,borderRadius:2,background:T.edge,margin:"0 auto 24px"}}/>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:20}}>
          <div style={{width:52,height:52,borderRadius:"50%",background:c+"18",border:`2px solid ${c}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:19,fontWeight:700,color:c,fontFamily:"'Crimson Pro',serif",flexShrink:0,overflow:"hidden"}}>{person.photo?<img src={person.photo} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} alt=""/>:(person.ini||person.name[0])}</div>
          <div>
            <p style={{fontSize:17,fontWeight:600,color:T.ink,fontFamily:"'Crimson Pro',serif"}}>{person.name}</p>
            <p style={{fontSize:12,color:T.muted,marginTop:2}}>Remove from your orbit?</p>
          </div>
        </div>
        <div style={{background:T.red+"0C",border:`1px solid ${T.red}25`,borderRadius:12,padding:"12px 14px",marginBottom:20}}>
          <p style={{fontSize:12,color:T.red,lineHeight:1.5}}>This will remove <strong>{person.name}</strong> and all their shared history — fairness data, agreements, and scheduled events. This can't be undone.</p>
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={onCancel} style={{flex:1,padding:"12px",borderRadius:12,background:T.mist,border:`1px solid ${T.edge}`,color:T.bark,fontSize:13,fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={onConfirm} style={{flex:1,padding:"12px",borderRadius:12,background:T.red,border:"none",color:"white",fontSize:13,fontWeight:600,cursor:"pointer"}}>Remove {person.name}</button>
        </div>
      </div>
    </div>
  );
}

function monthlyTgt(p) { return p.tgtMode === "week" ? Math.round(p.tgt * 4.33) : p.tgt; }

export function PersonDrawer({personId, onClose, onSchedule, isMobile}) {
  const {people:PEOPLE,removePerson,updatePerson,priv,privOverrides,setPrivOverrides,calEvents} = usePeople();
  const p = PEOPLE.find(x=>x.id===personId);
  const [dtab,setDtab] = useState("overview");
  const privacy = privOverrides[personId]?.privOver || (p ? priv[p.type] : "avail");
  const setPrivacy = (val) => setPrivOverrides(o=>({...o,[personId]:{...(o[personId]||{}),privOver:val}}));
  const notifOn = privOverrides[personId]?.notif !== false;
  const setNotifOn = (v) => setPrivOverrides(o=>({...o,[personId]:{...(o[personId]||{}),notif:v}}));
  const anchorOn = privOverrides[personId]?.anchorOn !== false;
  const setAnchorOn = (v) => setPrivOverrides(o=>({...o,[personId]:{...(o[personId]||{}),anchorOn:v}}));
  const [nickname,setNickname] = useState("");
  const [confirmRemove,setConfirmRemove] = useState(false);
  const [isEditing,setIsEditing] = useState(false);
  const [pingMsg,setPingMsg] = useState("");
  const [pingState,setPingState] = useState("idle"); // idle|composing|sending|sent|error
  const [anniversaryOverride,setAnniversaryOverride] = useState(undefined);
  const [nickDraft,setNickDraft] = useState("");
  const [bdayDraft,setBdayDraft] = useState("");
  const [annexDraft,setAnnexDraft] = useState("");
  const [tgtDraft,setTgtDraft] = useState("");
  const [typeDraft,setTypeDraft] = useState("");

  if(!p) return null;
  const cfg = REL[p.type];
  const c = cfg.color;
  const effectiveAnniv = anniversaryOverride !== undefined ? anniversaryOverride : p.anniversary;
  const anniv = effectiveAnniv ? fmtAnniversary(effectiveAnniv) : null;
  const pct = p.tgt ? Math.round((p.act/p.tgt)*100) : null;
  const pctColor = pct>=90?T.sage:pct>=60?T.amber:T.red;
  const bday = p.birthday ? fmtBirthday(p.birthday) : null;
  const upcoming = PERSON_UPCOMING[p.id] || [];
  const note = PERSON_NOTES[p.id] || "";
  const nowH = new Date().getHours();
  const todayEvts = (calEvents||[]).filter(e=>e.person===p.id&&e.day===HOME_TODAY);
  const busyNow = todayEvts.some(e=>nowH>=e.startH&&nowH<e.endH);
  const currentEvt = todayEvts.find(e=>nowH>=e.startH&&nowH<e.endH);
  const availLabel = busyNow ? `Not free now · ${currentEvt?.label||"Busy"}` : "Free now";
  const availColor = busyNow ? T.amber : T.sage;

  const enterEdit = () => { setNickDraft(nickname); setBdayDraft(p.birthday||""); setAnnexDraft(effectiveAnniv||""); setTgtDraft(p.tgt!=null?String(p.tgt):""); setTypeDraft(p.type); setIsEditing(true); };
  const saveEdit = () => { setNickname(nickDraft.trim()); updatePerson(p.id,{birthday:bdayDraft.trim()||null,type:typeDraft||p.type}); setAnniversaryOverride(annexDraft.trim()||null); const t=parseInt(tgtDraft,10); if(!isNaN(t)&&t>0) updatePerson(p.id,{tgt:t}); setIsEditing(false); };

  const handlePing = async () => {
    if (!pingMsg.trim() || !p.linkedUserId) return;
    setPingState("sending");
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch("/api/connections/ping", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ to_user_id: p.linkedUserId, message: pingMsg.trim() }),
        signal: AbortSignal.timeout(15_000),
      });
      if (res.ok) {
        setPingState("sent");
        setPingMsg("");
        setTimeout(() => setPingState("idle"), 3000);
      } else {
        const body = await res.json().catch(() => ({}));
        console.error("[ping]", body.error);
        setPingState("error");
      }
    } catch (err) {
      console.error("[ping]", err);
      setPingState("error");
    }
  };

  const tabs = [
    {id:"overview",  label:"Overview"},
    {id:"schedule",  label:"Schedule"},
    {id:"agreements",label:"Agreements"},
    {id:"privacy",   label:"Privacy"},
  ];

  return (<>
    <div onClick={onClose} style={{position:isMobile?"absolute":"fixed",inset:0,background:"rgba(30,16,8,0.35)",zIndex:400,animation:"fadeIn .2s ease"}}/>

    <div style={{position:isMobile?"absolute":"fixed",top:0,right:0,bottom:0,width:isMobile?"100%":"min(480px,100vw)",background:T.bg,zIndex:401,display:"flex",flexDirection:"column",boxShadow:"-8px 0 48px rgba(30,16,8,.18)",animation:"slideInRight .25s ease",overflowY:"auto"}}>

      <div style={{background:T.surface,borderBottom:`1px solid ${T.edge}`,padding:"20px 24px 0",paddingTop:"calc(20px + env(safe-area-inset-top, 0px))",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:16,marginBottom:16}}>
          <div style={{position:"relative",flexShrink:0}}>
            <div style={{width:64,height:64,borderRadius:"50%",background:c+"20",border:`2.5px solid ${c}70`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,fontWeight:700,color:c,fontFamily:"'Crimson Pro',serif",overflow:"hidden"}}>
              {p.photo?<img src={p.photo} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} alt=""/>:p.ini}
            </div>
            {p.linkedUserId&&<p style={{fontSize:10,color:T.muted,textAlign:"center",marginTop:4}}>Last seen: {_fmtLastSeen(p.lastActive)||"—"}</p>}
          </div>

          <div style={{flex:1,minWidth:0}}>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
              {isEditing
                ?<input value={nickDraft} onChange={e=>setNickDraft(e.target.value)} placeholder={p.name} style={{flex:1,fontSize:18,fontFamily:"'Crimson Pro',serif",fontWeight:600,color:T.ink,background:T.mist,border:`1.5px solid ${c}60`,borderRadius:8,padding:"3px 8px",outline:"none",minWidth:0}}/>
                :<div style={{display:"flex",alignItems:"center",gap:7,flex:1,minWidth:0}}>
                    <h2 style={{fontSize:22,fontFamily:"'Crimson Pro',serif",fontWeight:600,color:T.ink,letterSpacing:"-0.01em",lineHeight:1}}>{nickname||p.name}</h2>
                    {nickname&&<span style={{fontSize:11,color:T.muted,fontStyle:"italic",lineHeight:1}}>{p.name}</span>}
                  </div>
              }
              {isEditing
                ? null
                : <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:c+"18",border:`1px solid ${c}30`,color:c,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em",flexShrink:0}}>{cfg.label}</span>
              }
            </div>
            {isEditing && (
              <div style={{display:"flex",gap:5,flexWrap:"wrap",marginBottom:6,marginTop:4}}>
                {Object.entries(REL).map(([key,rcfg])=>(
                  <button key={key} onClick={()=>setTypeDraft(key)} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 9px",borderRadius:20,border:`1.5px solid ${typeDraft===key?rcfg.color:T.edge}`,background:typeDraft===key?rcfg.color+"14":T.surface,color:typeDraft===key?rcfg.color:T.muted,fontSize:11,fontWeight:typeDraft===key?700:400,cursor:"pointer",transition:"all .13s"}}>
                    <span>{rcfg.icon}</span>{rcfg.label}
                  </button>
                ))}
              </div>
            )}
            {!isEditing && <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:6}}>
              {p.tags.map(t=><Tag key={t} label={t} color={c}/>)}
            </div>}
            <p style={{fontSize:11,color:availColor,fontWeight:500}}>● {availLabel}</p>
          </div>

          <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
            <button onClick={onClose} style={{width:28,height:28,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:13,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
            <button onClick={isEditing?saveEdit:enterEdit} style={{padding:"6px 12px",borderRadius:10,background:isEditing?T.sage:T.mist,border:`1px solid ${isEditing?T.sage:T.edge}`,color:isEditing?"white":T.muted,fontSize:11,fontWeight:600,cursor:"pointer",whiteSpace:"nowrap",transition:"all .15s"}}>{isEditing?"Save ✓":"Edit"}</button>
          </div>
        </div>

        <div style={{display:"flex",gap:0,marginBottom:-1,opacity:isEditing?0.35:1,pointerEvents:isEditing?"none":"auto",transition:"opacity .15s"}}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setDtab(t.id)} style={{padding:"8px 14px",border:"none",cursor:"pointer",background:"transparent",color:dtab===t.id?T.ink:T.muted,fontSize:12,fontWeight:dtab===t.id?600:400,borderBottom:dtab===t.id?`2px solid ${c}`:"2px solid transparent",marginBottom:0,transition:"all .14s"}}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{flex:1,padding:"20px 24px",overflowY:"auto"}}>

        {dtab==="overview"&&(<div style={{animation:"fadeUp .2s ease",display:"flex",flexDirection:"column",gap:16}}>

          {/* Birthday */}
          <div>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Birthday</p>
            {isEditing
              ?<input type="date" value={bdayDraft} onChange={e=>setBdayDraft(e.target.value)} style={{width:"100%",padding:"7px 10px",borderRadius:9,border:`1.5px solid ${c}60`,background:T.surface,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"}}/>
              :<span style={{fontSize:13,color:T.bark,fontWeight:500}}>
                {bday ? `${bday.label} · ${bday.ageStr} old${bday.upcoming?` · in ${bday.diffDays}d`:""}` : "—"}
              </span>
            }
          </div>

          {/* Anniversary — partners only */}
          {(p.type==="partner"||p.anniversary)&&<div>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Anniversary</p>
            {isEditing
              ?<input type="date" value={annexDraft} onChange={e=>setAnnexDraft(e.target.value)} style={{width:"100%",padding:"7px 10px",borderRadius:9,border:`1.5px solid ${c}60`,background:T.surface,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"}}/>
              :<span style={{fontSize:13,color:T.bark,fontWeight:500}}>
                {anniv ? `${anniv.label} · ${anniv.yearStr}${anniv.upcoming?` · in ${anniv.diffDays}d`:""}` : "—"}
              </span>
            }
          </div>}

          {/* Fairness tracking — partners only */}
          {(p.type==="partner")&&<div>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>Fairness tracking</p>
            {isEditing
              ?<div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:11,color:T.muted}}>Target nights/mo:</span>
                <input type="number" value={tgtDraft} onChange={e=>setTgtDraft(e.target.value)} min="1" max="31" placeholder="—" style={{width:48,padding:"3px 7px",borderRadius:7,border:`1.5px solid ${c}50`,background:T.mist,fontSize:13,color:T.ink,outline:"none",textAlign:"center"}}/>
              </div>
              :<span style={{fontSize:13,color:T.bark,fontWeight:500}}>
                {p.tgt ? `${Math.round(p.act/monthlyTgt(p)*100)}% · ${p.act}/${monthlyTgt(p)} nights` : "Not tracked"}
              </span>
            }
          </div>}

          {/* Next date */}
          <div style={{display:"flex",justifyContent:"space-between",fontSize:13,paddingBottom:12,borderBottom:`1px solid ${T.edge}`}}>
            <span style={{color:T.muted}}>Next date</span>
            <span style={{fontWeight:500,color:T.bark}}>{p.next||"TBD"}</span>
          </div>

          {/* Upcoming */}
          {upcoming.length>0&&<div>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Upcoming</p>
            {upcoming.map((ev,i)=><div key={i} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:11,background:T.surface,border:`1px solid ${T.edge}`,marginBottom:6}}>
              <div style={{width:8,height:8,borderRadius:"50%",background:c,flexShrink:0}}/>
              <div style={{flex:1}}><p style={{fontSize:13,fontWeight:500,color:T.ink}}>{ev.label}</p><p style={{fontSize:11,color:T.muted}}>{ev.sub}</p></div>
            </div>)}
          </div>}

          {/* Notes */}
          {note&&<div>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Notes</p>
            <div style={{background:T.surface,borderRadius:11,padding:"12px 14px",border:`1px solid ${T.edge}`}}>
              <p style={{fontSize:13,color:T.bark,lineHeight:1.6,fontStyle:"italic"}}>"{note}"</p>
            </div>
          </div>}

          {/* Icon color — edit only, no photo */}
          {isEditing&&!p.photo&&<div>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Icon color</p>
            <div style={{display:"flex",gap:7,flexWrap:"wrap",alignItems:"center"}}>
              {["#C17B4E","#7A9E7E","#4A8FA8","#9B6BB5","#C4703A","#5B8C6B","#D4956A","#6B8FAA","#B56B6B","#7B9B6B","#8B6BAA","#4A7B8C"].map(col=>{
                const isActive=(p.avatarColor||cfg.color)===col;
                return(<div key={col} onClick={()=>updatePerson(p.id,{avatarColor:col})}
                  style={{width:26,height:26,borderRadius:"50%",background:col,cursor:"pointer",border:isActive?`3px solid ${T.ink}`:`2px solid ${col}88`,boxShadow:isActive?`0 0 0 2px white inset`:"none",transition:"all .12s",flexShrink:0}}/>);
              })}
              <label title="Custom color" style={{width:26,height:26,borderRadius:"50%",background:`conic-gradient(red,yellow,lime,cyan,blue,magenta,red)`,cursor:"pointer",border:`2px solid ${T.edge}`,flexShrink:0,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <input type="color" value={p.avatarColor||cfg.color} onChange={e=>updatePerson(p.id,{avatarColor:e.target.value})} style={{opacity:0,width:0,height:0,position:"absolute"}}/>
              </label>
              {p.avatarColor&&<button onClick={()=>updatePerson(p.id,{avatarColor:null})} style={{fontSize:11,color:T.muted,background:"none",border:"none",cursor:"pointer",textDecoration:"underline",marginLeft:2}}>Reset</button>}
            </div>
          </div>}

          {/* Actions at bottom */}
          <div style={{display:"flex",gap:8,paddingTop:4}}>
            <button onClick={()=>setConfirmRemove(true)} style={{padding:"9px 13px",borderRadius:10,background:"transparent",border:`1px solid ${T.edge}`,color:T.red,fontSize:12,cursor:"pointer"}}>Remove</button>
            <button onClick={onSchedule} style={{flex:1,padding:"10px",borderRadius:10,background:c,border:"none",color:"white",fontSize:13,fontWeight:600,cursor:"pointer",boxShadow:`0 3px 12px ${c}44`}}>Schedule →</button>
          </div>

          {/* Ping — only for connected Orbit users */}
          {p.linkedUserId&&<div style={{paddingTop:4}}>
            {pingState==="idle"&&<button onClick={()=>setPingState("composing")} style={{width:"100%",padding:"9px",borderRadius:10,background:T.surface,border:`1px solid ${T.edge}`,color:T.muted,fontSize:12,cursor:"pointer",textAlign:"left"}}>Send a ping — let them know you're thinking of them</button>}
            {pingState==="composing"&&<div style={{background:T.surface,border:`1px solid ${T.edge}`,borderRadius:12,padding:"12px 14px"}}>
              <textarea
                value={pingMsg}
                onChange={e=>setPingMsg(e.target.value.slice(0,140))}
                placeholder="Thinking of you…"
                rows={2}
                style={{width:"100%",resize:"none",border:"none",background:"transparent",fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"}}
              />
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginTop:6}}>
                <span style={{fontSize:11,color:pingMsg.length>120?T.amber:T.muted}}>{pingMsg.length}/140</span>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>{setPingState("idle");setPingMsg("");}} style={{padding:"5px 12px",borderRadius:8,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted,fontSize:12,cursor:"pointer"}}>Cancel</button>
                  <button onClick={handlePing} disabled={!pingMsg.trim()} style={{padding:"5px 14px",borderRadius:8,background:pingMsg.trim()?c:T.edge,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:pingMsg.trim()?"pointer":"default",transition:"background .15s"}}>Send</button>
                </div>
              </div>
            </div>}
            {pingState==="sending"&&<p style={{fontSize:12,color:T.muted,textAlign:"center",padding:"8px 0"}}>Sending…</p>}
            {pingState==="sent"&&<p style={{fontSize:12,color:T.sage,textAlign:"center",padding:"8px 0",fontWeight:500}}>Ping sent!</p>}
            {pingState==="error"&&<div style={{background:T.red+"0C",border:`1px solid ${T.red}25`,borderRadius:10,padding:"10px 14px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <p style={{fontSize:12,color:T.red}}>Failed to send. Try again?</p>
              <button onClick={()=>setPingState("composing")} style={{fontSize:11,color:T.red,background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}}>Retry</button>
            </div>}
          </div>}
        </div>)}

        {dtab==="schedule"&&(<div style={{animation:"fadeUp .2s ease"}}>
          <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>This week with {p.name}</p>
          {HOME_WEEK_DAYS.map((dayAbbr,i)=>{
            const dateNum=WEEK_DATE_NUMS[i];
            const hasEvent=HOME_EVENTS.some(e=>e.personId===p.id&&e.day===i)||upcoming.some((_,ui)=>ui===0&&i===0);
            const isToday=i===HOME_TODAY;
            return(<div key={dayAbbr+dateNum} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:11,background:hasEvent?c+"0C":T.surface,border:`1px solid ${hasEvent?c+"30":T.edge}`,marginBottom:6}}>
              <div style={{width:36,textAlign:"center",flexShrink:0}}>
                <p style={{fontSize:11,fontWeight:600,color:isToday?c:T.muted,textTransform:"uppercase"}}>{dayAbbr}</p>
                <p style={{fontSize:16,fontFamily:"'Crimson Pro',serif",color:isToday?c:T.ink,fontWeight:isToday?700:300}}>{dateNum}</p>
              </div>
              <div style={{flex:1}}>
                {hasEvent
                  ?<p style={{fontSize:13,fontWeight:500,color:c}}>{p.id===1&&i===0?"Dinner + overnight":p.id===1&&i===5?"Anchor night":upcoming[0]?.sub||"Time together"}</p>
                  :<p style={{fontSize:12,color:T.muted}}>Open</p>
                }
              </div>
              {!hasEvent&&<button onClick={onSchedule} style={{padding:"4px 10px",borderRadius:8,background:c+"14",border:`1px solid ${c}25`,color:c,fontSize:11,fontWeight:500,cursor:"pointer"}}>+ Add</button>}
            </div>);
          })}
          <button onClick={onSchedule} style={{width:"100%",marginTop:8,padding:"11px",borderRadius:11,background:c,border:"none",color:"white",fontSize:13,fontWeight:600,cursor:"pointer"}}>Schedule with {p.name} →</button>
        </div>)}

        {dtab==="agreements"&&(<PersonAgreements p={p} c={c}/>)}

        {dtab==="privacy"&&(<div style={{animation:"fadeUp .2s ease"}}>
          <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Calendar visibility</p>
          <p style={{fontSize:12,color:T.muted,marginBottom:14,lineHeight:1.5}}>What {p.name} can see on your calendar</p>
          <div style={{marginBottom:20}}>
            {PRIVACY_OPTS.map(opt=>(
              <div key={opt.id} onClick={()=>setPrivacy(opt.id)} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",borderRadius:11,background:privacy===opt.id?c+"0C":T.surface,border:`1.5px solid ${privacy===opt.id?c+"50":T.edge}`,marginBottom:6,cursor:"pointer",transition:"all .14s"}}>
                <span style={{fontSize:18,color:privacy===opt.id?c:T.muted}}>{opt.icon}</span>
                <div style={{flex:1}}>
                  <p style={{fontSize:13,fontWeight:privacy===opt.id?600:400,color:privacy===opt.id?T.ink:T.bark}}>{opt.label}</p>
                  <p style={{fontSize:11,color:T.muted}}>{opt.desc}</p>
                </div>
                <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${privacy===opt.id?c:T.edgeMid}`,background:privacy===opt.id?c:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  {privacy===opt.id&&<div style={{width:7,height:7,borderRadius:"50%",background:"white"}}/>}
                </div>
              </div>
            ))}
          </div>

          <div style={{height:1,background:T.edge,marginBottom:16}}/>
          <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12}}>Notifications</p>
          {[
            {label:"Availability alerts",sub:`When ${p.name} becomes free`,on:notifOn,set:setNotifOn},
            {label:"Anchor night reminders",sub:"24h before recurring time",on:anchorOn,set:setAnchorOn},
          ].map((row,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 0",borderBottom:i===0?`1px solid ${T.edge}`:"none"}}>
              <div><p style={{fontSize:13,fontWeight:500,color:T.ink}}>{row.label}</p><p style={{fontSize:11,color:T.muted,marginTop:1}}>{row.sub}</p></div>
              <Tog on={row.on} set={row.set} c={c}/>
            </div>
          ))}

          {p.type==="partner"&&p.tgt>0&&<>
            <div style={{height:1,background:T.edge,margin:"16px 0"}}/>
            <p style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:4}}>Fairness alert frequency</p>
            <p style={{fontSize:12,color:T.muted,marginBottom:12,lineHeight:1.5}}>How often to be reminded when fairness falls below 60% for {p.name}</p>
            {[
              {id:null,   label:"Default",       sub:"Use app-wide setting"},
              {id:"never",label:"Never",          sub:"Disable fairness alerts for this person"},
              {id:"daily",label:"Daily",          sub:"Every day while below threshold"},
              {id:"weekly",label:"Weekly",        sub:"At most once per week"},
              {id:"biweekly",label:"Every 2 weeks",sub:"At most once every two weeks"},
              {id:"monthly",label:"Monthly",      sub:"At most once per month"},
            ].map(opt=>(
              <div key={String(opt.id)} onClick={()=>updatePerson(p.id,{fairnessNotifFreq:opt.id})} style={{display:"flex",alignItems:"center",gap:12,padding:"10px 14px",borderRadius:10,background:p.fairnessNotifFreq===opt.id?c+"0C":T.surface,border:`1.5px solid ${p.fairnessNotifFreq===opt.id?c+"50":T.edge}`,marginBottom:6,cursor:"pointer",transition:"all .14s"}}>
                <div style={{flex:1}}>
                  <p style={{fontSize:13,fontWeight:p.fairnessNotifFreq===opt.id?600:400,color:p.fairnessNotifFreq===opt.id?T.ink:T.bark}}>{opt.label}</p>
                  <p style={{fontSize:11,color:T.muted}}>{opt.sub}</p>
                </div>
                <div style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${p.fairnessNotifFreq===opt.id?c:T.edgeMid}`,background:p.fairnessNotifFreq===opt.id?c:"transparent",flexShrink:0}}/>
              </div>
            ))}
          </>}
        </div>)}

      </div>

      {confirmRemove&&<RemovePersonDialog person={{...p,name:nickname||p.name}} onConfirm={()=>{removePerson(p.id);onClose();}} onCancel={()=>setConfirmRemove(false)}/>}

    </div>
  </>);
}
